import { faMessage } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Divider, IconBadge, IconBadgeUse, Text } from '@growtherapy/sprout-ui';

import { ModifiedToastAria } from '../toasts';
import { Toast } from '../toasts/Toast';
import { ToastCloseButton, ToastCloseButtonProps } from '../toasts/ToastCloseButton';

export type NewMessageToastProps = {
  senderName: string;
  messageContent: string;
  onOpenChatDrawer: () => void;
  quickRepliesComponent?: ReactNode;
  className?: string;
  closeButtonProps?: ToastCloseButtonProps;
} & ModifiedToastAria;

export function NewMessageToast({
  senderName,
  messageContent,
  onOpenChatDrawer,
  quickRepliesComponent,
  contentProps,
  descriptionProps,
  titleProps,
  closeButtonProps,
  ...toastAriaProps
}: NewMessageToastProps) {
  const t = useTranslate();
  return (
    <Toast className='sm:w-[30rem]' alignment='top' closeButtonSize='sm' {...toastAriaProps}>
      <div className='flex flex-col w-full gap-4'>
        <div className='flex flex-row gap-4 justify-between'>
          <button
            className='flex gap-4 w-full align-start fs-exclude min-w-0'
            data-dd-privacy='mask'
            data-dd-action-name='Click on new message toast button'
            onClick={onOpenChatDrawer}
            aria-label={t('telehealth-core:chatDrawer.openMessage')}
          >
            <IconBadge aria-hidden icon={faMessage} size='lg' use={IconBadgeUse.Lilac} />
            <div className='flex flex-col min-w-0' {...contentProps}>
              <Text
                {...titleProps}
                data-dd-privacy='mask'
                data-dd-action-name='Click on new message toast title'
                className='grow font-medium text-lilac-700 text-left fs-exclude rebrand:text-text-default'
              >
                <span className='sr-only'>
                  {t('telehealth-core:chatDrawer.newMessageFromSenderName', {
                    senderName,
                  })}
                </span>
                <span aria-hidden>{senderName}</span>
              </Text>
              <Text
                {...descriptionProps}
                data-dd-privacy='mask'
                data-dd-action-name='Click on new message toast body'
                variant='sm'
                className='line-clamp-2 sm:line-clamp-3 text-neutral-800 text-left fs-exclude break-words rebrand:text-text-default'
              >
                {messageContent}
              </Text>
            </div>
          </button>
          <ToastCloseButton size='sm' {...closeButtonProps} />
        </div>
        {quickRepliesComponent && (
          <>
            <Divider className='w-full border-neutral-500 rebrand:border-border-subtle' />
            {quickRepliesComponent}
          </>
        )}
      </div>
    </Toast>
  );
}
